import { useParams } from 'react-router-dom';
import { Stack, useToast, useDisclosure, Collapse, Button, Input, Flex, Text } from '@chakra-ui/react';
import { FileText, Link as LinkIcon, Type } from 'lucide-react';
import { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperclip, faTrashRestore, faCheck, faRedo, faEdit, faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import { Spinner } from '@chakra-ui/react';
import AddUrl from 'components/modals/AddUrl';
import ReadFullText from 'components/modals/ReadFullText';
import ConfirmDialog from 'components/modals/ConfirmDialog';
import { ChevronDownIcon } from '@chakra-ui/icons';
import useServerApi from 'hooks/useServerApi';

interface FullAgentConfigKnowledgeBaseProps {
    agentId?: string;
}

const checkFileSize = (file: File) => {
    const maxSize = 100 * 1024 * 1024;
    return file.size <= maxSize;
};

const FullAgentConfigKnowledgeBase = ({ agentId: agentIdProp }: FullAgentConfigKnowledgeBaseProps) => {
    const toast = useToast();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const params = useParams();
    const agentId = agentIdProp || (params.id as string);
    const [{ post, get }] = useServerApi();

    const { isOpen: isCollapseOpen, onClose: onCollapseClose, onOpen: onCollapseOpen, onToggle } = useDisclosure();
    const { isOpen: isUrlOpen, onOpen: onUrlOpen, onClose: onUrlClose } = useDisclosure();
    const { isOpen: isFullTextOpen, onOpen: onFullTextOpen, onClose: onFullTextClose } = useDisclosure();

    const [value, setValue] = useState('');
    const [fullText, setFullText] = useState('');
    // refetch frequency for Link uploads: 'none' or one of TASK_FREQUENCY_CHOICES ('daily','weekly','monthly','yearly')
    const [refetchFrequency, setRefetchFrequency] = useState('none');
    const [knowledgeId, setKnowledgeId] = useState('');
    const [isOpenAddText, setIsOpenAddText] = useState(false);
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [uploadingItemIds, setUploadingItemIds] = useState<Set<string>>(new Set());
    const [knowledgeArray, setKnowledgeArray] = useState<any[]>([]);
    const [knowledgeLoading, setKnowledgeLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [activeLoading, setActiveLoading] = useState(false);

    const [editingKnowledgeId, setEditingKnowledgeId] = useState<string | null>(null);
    const fetchKnowledgeData = useCallback(async () => {
        setKnowledgeLoading(true);
        try {
            const [success, data] = await get(`/rest_endpoints/agents/knowledge/${agentId}`);
            if (success && data?.knowledgeData) {
                setKnowledgeArray(data.knowledgeData);
            }
        } catch (error) {
            console.error('Error fetching knowledge data:', error);
        } finally {
            setKnowledgeLoading(false);
        }
    }, [agentId, get]);
    useEffect(() => {
        if (isCollapseOpen) {
            fetchKnowledgeData();
        }
    }, [agentId, isCollapseOpen, fetchKnowledgeData]);

    const pollUploadStatus = useCallback(async (knowledgeDataId: string, onSuccess?: () => void) => {
        const pollInterval = 3000;
        const maxAttempts = 30;
        let attempts = 0;

        const checkStatus = async () => {
            try {
                attempts += 1;

                const [ok, data] = await get(`/rest_endpoints/vector_db/upload_status/${knowledgeDataId}`);

                if (!ok || !data) {
                    if (attempts < maxAttempts) {
                        setTimeout(checkStatus, pollInterval);
                    } else {
                        setUploadingItemIds(prev => {
                            const updated = new Set(prev);
                            updated.delete(knowledgeDataId);
                            return updated;
                        });
                    }
                    return;
                }

                const status = data.status;

                if (status === 'SUCCESS') {
                    setUploadingItemIds(prev => {
                        const updated = new Set(prev);
                        updated.delete(knowledgeDataId);
                        return updated;
                    });

                    setKnowledgeArray(prev =>
                        prev.map((k: any) =>
                            k.id === knowledgeDataId
                                ? { ...k, processingStatus: 'Success' }
                                : k
                        )
                    );

                    if (onSuccess) onSuccess();
                    return;
                } else if (status === 'FAILURE') {
                    setUploadingItemIds(prev => {
                        const updated = new Set(prev);
                        updated.delete(knowledgeDataId);
                        return updated;
                    });
                    toast({
                        title: 'Upload failed',
                        status: 'error',
                        isClosable: true,
                    });
                    return;
                }

                if (attempts < maxAttempts) {
                    setTimeout(checkStatus, pollInterval);
                } else {
                    setUploadingItemIds(prev => {
                        const updated = new Set(prev);
                        updated.delete(knowledgeDataId);
                        return updated;
                    });
                }
            } catch (err) {
                if (attempts < maxAttempts) {
                    setTimeout(checkStatus, pollInterval);
                } else {
                    setUploadingItemIds(prev => {
                        const updated = new Set(prev);
                        updated.delete(knowledgeDataId);
                        return updated;
                    });
                }
            }
        };

        checkStatus();
    }, [get, toast]);

    const onRefetchNow = useCallback(async (item: any) => {
        try {
            // indicate uploading/refetching
            setUploadingItemIds(prev => new Set(prev).add(item.id));

            const payload: any = {
                url: item.source,
                agent_id: agentId,
                refetch_frequency: item.recurringFrequency || 'none',
                knowledge_data_id: item.id // include current id so backend can associate
            };

            const [success, data] = await post('/rest_endpoints/vector_db/upload_url', payload);

            if (success) {
                toast({ title: data?.message || 'Refetch started', status: 'success', isClosable: true });
                if (data?.task_id && data?.knowledge_data_id) {
                    pollUploadStatus(data.knowledge_data_id, data.task_id);
                }
                fetchKnowledgeData();
            } else {
                toast({ title: data?.message || 'Refetch failed', status: 'error', isClosable: true });
            }
        } catch (err) {
            console.error('Error refetching URL:', err);
            toast({ title: 'Error refetching URL', status: 'error', isClosable: true });
        } finally {
            setUploadingItemIds(prev => {
                const copy = new Set(prev);
                copy.delete(item.id);
                return copy;
            });
        }
    }, [post, toast, agentId, pollUploadStatus, fetchKnowledgeData]);

    const onUploadImage = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const file = e.target.files[0];
            if (checkFileSize(file)) {
                setUploadedFile(file);
            } else {
                toast({
                    title: 'Max file size is 100MB',
                    status: 'error',
                    isClosable: true,
                });
            }
        }
    };

    const handleFileClick = () => {
        fileInputRef.current?.click();
    };

    const loadFile = useCallback(async (file: File) => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('agent_id', agentId);

            const [success, data] = await post('/rest_endpoints/vector_db/upload_file', formData);

            if (success && data.knowledge_data_id) {
                const knowledgeDataId = data.knowledge_data_id;
                setUploadingItemIds(prev => new Set(prev).add(knowledgeDataId));

                setKnowledgeArray(prev => [...prev, {
                    id: knowledgeDataId,
                    source: file.name,
                    sourceType: 'File',
                    text: '',
                    active: true,
                    processingStatus: 'Processing',
                }]);

                pollUploadStatus(knowledgeDataId, () => {
                    toast({
                        title: 'File uploaded successfully',
                        status: 'success',
                        isClosable: true,
                    });
                    fetchKnowledgeData();
                });
            } else {
                toast({
                    title: data?.message || 'Upload failed',
                    status: 'error',
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error uploading file:', error);
            toast({
                title: 'Error uploading file',
                status: 'error',
                isClosable: true,
            });
        }
    }, [agentId, post, toast, fetchKnowledgeData, pollUploadStatus]);

    const loadUrl = useCallback(async (url: string) => {
        try {
            const payload: any = {
                url,
                agent_id: agentId,
                refetch_frequency: refetchFrequency || 'none'
            };

            if (editingKnowledgeId) payload.knowledge_data_id = editingKnowledgeId;

            const [success, data] = await post('/rest_endpoints/vector_db/upload_url', payload);

            if (success && data.knowledge_data_id) {
                const knowledgeDataId = data.knowledge_data_id;
                setValue('');
                onUrlClose();
                setUploadingItemIds(prev => new Set(prev).add(knowledgeDataId));
                if (!editingKnowledgeId) {
                    setKnowledgeArray(prev => [...prev, {
                        id: knowledgeDataId,
                        source: url,
                        sourceType: 'Link',
                        text: '',
                        active: true,
                        processingStatus: 'Processing',
                        // optimistic recurrence fields
                        isRecurring: refetchFrequency && refetchFrequency !== 'none',
                        recurringFrequency: refetchFrequency && refetchFrequency !== 'none' ? refetchFrequency : null,
                    }]);
                }

                pollUploadStatus(knowledgeDataId, () => {
                    toast({
                        title: 'URL uploaded successfully',
                        status: 'success',
                        isClosable: true,
                    });
                    fetchKnowledgeData();
                });
            } else {
                toast({
                    title: data?.message || 'Upload failed',
                    status: 'error',
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error uploading URL:', error);
            toast({
                title: 'Error uploading URL',
                status: 'error',
                isClosable: true,
            });
        } finally {
            setEditingKnowledgeId(null);
        }
    }, [agentId, post, toast, onUrlClose, fetchKnowledgeData, pollUploadStatus, setValue, editingKnowledgeId, refetchFrequency]);

    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

    const performDeleteKnowledge = useCallback(async (id: string) => {
        try {
            setDeleteLoading(true);
            setKnowledgeId(id);
            const [success, data] = await post(`/rest_endpoints/vector_db/delete_knowledge/${id}`, {});

            if (success) {
                toast({
                    status: 'success',
                    isClosable: true,
                    title: 'Source deleted successfully',
                });

                setKnowledgeArray(prev => prev.filter(k => k.id !== id));
            } else {
                toast({
                    status: 'error',
                    isClosable: true,
                    title: 'Failed to delete source',
                });
            }
        } catch (error) {
            console.error('Error deleting knowledge:', error);
            toast({
                status: 'error',
                isClosable: true,
                title: 'Error deleting source',
            });
        } finally {
            setDeleteLoading(false);
        }
    }, [post, toast]);

    const deleteSpecificKnowledge = useCallback((id: string) => {
        setPendingDeleteId(id);
        setIsConfirmOpen(true);
    }, []);

    const handleConfirmDelete = () => {
        if (pendingDeleteId) performDeleteKnowledge(pendingDeleteId);
        setPendingDeleteId(null);
        setIsConfirmOpen(false);
    };

    const handleCancelDelete = () => {
        setPendingDeleteId(null);
        setIsConfirmOpen(false);
    };

    const onRetryLoad = useCallback(async (knowledgeDataId: string) => {
        try {
            // Use GraphQL retryUpload mutation via the generic post helper
            const mutation = `mutation retryUpload($knowledgeDataId: ID!) { retryUpload(knowledgeDataId: $knowledgeDataId) { __typename ... on SuccessType { message } ... on ErrorType { message } } }`;

            // indicate retrying with spinner
            setUploadingItemIds(prev => new Set(prev).add(knowledgeDataId));

            const [success, data] = await post('/graphql', { query: mutation, variables: { knowledgeDataId } });

            if (success && data?.data?.retryUpload) {
                const result = data.data.retryUpload;
                if (result.__typename === 'SuccessType') {
                    toast({ title: result.message || 'Retry started', status: 'success', isClosable: true });
                    // poll status again
                    pollUploadStatus(knowledgeDataId, () => {
                        fetchKnowledgeData();
                    });
                } else {
                    toast({ title: result.message || 'Retry failed', status: 'error', isClosable: true });
                    setUploadingItemIds(prev => {
                        const copy = new Set(prev);
                        copy.delete(knowledgeDataId);
                        return copy;
                    });
                }
            } else {
                toast({ title: data?.message || 'Retry failed', status: 'error', isClosable: true });
                setUploadingItemIds(prev => {
                    const copy = new Set(prev);
                    copy.delete(knowledgeDataId);
                    return copy;
                });
            }
        } catch (err) {
            console.error('Error retrying upload:', err);
            toast({ title: 'Error retrying upload', status: 'error', isClosable: true });
            setUploadingItemIds(prev => {
                const copy = new Set(prev);
                copy.delete(knowledgeDataId);
                return copy;
            });
        }
    }, [post, toast, pollUploadStatus, fetchKnowledgeData]);

    const isDisabled = useMemo(() => {
        return deleteLoading || activeLoading || uploadingItemIds.size > 0;
    }, [deleteLoading, activeLoading, uploadingItemIds]);

    useEffect(() => {
        if (uploadedFile) {
            loadFile(uploadedFile);
            setUploadedFile(null);
        }
    }, [uploadedFile, loadFile]);

    return (
        <Stack spacing='4'>
            <div className='pt-6 border-t'>
                <div className='flex items-start justify-between'>
                    <div>
                        <h3 className='text-lg font-semibold mb-2'>Agent Knowledge Base</h3>
                        <p className='text-sm text-[#71717a] mb-4'>
                            Manage your agent's knowledge sources
                        </p>
                    </div>
                    <Button
                        aria-label={isCollapseOpen ? 'Collapse knowledge base' : 'Expand knowledge base'}
                        size='sm'
                        colorScheme='teal'
                        onClick={onToggle}
                        leftIcon={<ChevronDownIcon transform={isCollapseOpen ? 'rotate(180deg)' : 'rotate(0deg)'} transition='transform 0.2s' />}
                    >
                        {isCollapseOpen ? 'Collapse' : 'Expand'}
                    </Button>
                </div>
            </div>

            <Collapse in={isCollapseOpen} animateOpacity>
                <Flex alignItems='center' justifyContent='space-between' flexWrap='wrap' gap='4' p='4' borderRadius='md' bg='white' mb='4'>
                    <Flex alignItems='center' gap='2'>
                        <Text className='text-sm font-medium'>Uploaded Sources</Text>
                        <Text className='text-sm text-gray-500'>({knowledgeArray.length})</Text>
                    </Flex>

                    <Stack direction='row' spacing='4' alignItems='center'>
                        <Flex
                            alignItems='center'
                            gap='1.5'
                            cursor='pointer'
                            onClick={() => { setFullText(''); setKnowledgeId(''); setIsOpenAddText(true); onFullTextOpen(); }}
                            _hover={{ opacity: 0.8 }}
                            transition='opacity 0.2s'
                        >
                            <Type size={14} color='#6b7280' />
                            <Text fontSize='sm' fontWeight='medium' color='gray.600'>Text</Text>
                        </Flex>
                        <Flex
                            alignItems='center'
                            gap='1.5'
                            cursor='pointer'
                            onClick={onUrlOpen}
                            _hover={{ opacity: 0.8 }}
                            transition='opacity 0.2s'
                        >
                            <LinkIcon size={14} color='#20c997' />
                            <Text fontSize='sm' fontWeight='medium' color='gray.600'>URL</Text>
                        </Flex>
                        <Flex
                            alignItems='center'
                            gap='1.5'
                            cursor='pointer'
                            onClick={handleFileClick}
                            _hover={{ opacity: 0.8 }}
                            transition='opacity 0.2s'
                            pos='relative'
                        >
                            <FileText size={14} color='#fd7e14' />
                            <Text fontSize='sm' fontWeight='medium' color='gray.600'>File</Text>
                            <Input
                                pos='absolute'
                                ref={fileInputRef}
                                accept='.doc, .docx, .pdf, .txt, .csv, .mp4, .mp3'
                                cursor='pointer'
                                w='full'
                                h='full'
                                type='file'
                                top='-1000px'
                                opacity={0}
                                onChange={onUploadImage}
                            />
                        </Flex>
                    </Stack>
                </Flex>
                {knowledgeLoading && (
                    <Flex justifyContent='center' p='4'>
                        <Spinner />
                    </Flex>
                )}

                {knowledgeArray.length > 0 ? (
                    <div className='mt-4'>
                        <div className='space-y-2'>
                            {knowledgeArray.map((item: any) => (
                                <div
                                    key={item.id}
                                    className='flex items-center justify-between p-3 border rounded bg-gray-50'
                                    onClick={() => { setFullText(''); setKnowledgeId(item.id); setIsOpenAddText(false); onFullTextOpen(); }}
                                    style={{ cursor: uploadingItemIds.has(item.id) ? 'not-allowed' : 'pointer', opacity: uploadingItemIds.has(item.id) ? 0.6 : 1 }}
                                >
                                    <div className='flex items-start gap-3 flex-1'>
                                        <div className='pt-1 text-gray-400'>
                                            {uploadingItemIds.has(item.id) ? (
                                                <Spinner size='sm' />
                                            ) : item.sourceType === 'Link' ? (
                                                <LinkIcon size={16} color='#6b7280' />
                                            ) : (
                                                <FontAwesomeIcon icon={faPaperclip} style={{ width: 16, height: 16, color: '#6b7280' }} />
                                            )}
                                        </div>
                                        <div className='min-w-0'>
                                            <Text className='text-sm font-medium' noOfLines={1}>
                                                {item.sourceType === 'Link' ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <a
                                                            href={item.source}
                                                            target='_blank'
                                                            rel='noopener noreferrer'
                                                            onClick={(e) => e.stopPropagation()}
                                                            style={{ color: '#0ea5a4', textDecoration: 'underline' }}
                                                        >
                                                            {item.source}
                                                        </a>
                                                        <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                                                            <button
                                                                aria-label='Edit URL'
                                                                className='p-1 rounded'
                                                                onClick={() => {
                                                                    setValue(item.source || '');
                                                                    setRefetchFrequency(item.recurringFrequency || 'none');
                                                                    setEditingKnowledgeId(item.id);
                                                                    onUrlOpen();
                                                                }}
                                                                style={{ background: 'transparent', border: 'none', color: '#6b7280' }}
                                                            >
                                                                <FontAwesomeIcon icon={faEdit} style={{ width: 14, height: 14 }} />
                                                            </button>
                                                            <button
                                                                aria-label='Refetch Now'
                                                                className='p-1 rounded'
                                                                onClick={() => onRefetchNow(item)}
                                                                style={{ background: 'transparent', border: 'none', color: '#0ea5a4' }}
                                                            >
                                                                <FontAwesomeIcon icon={faSyncAlt} style={{ width: 14, height: 14 }} />
                                                            </button>
                                                        </span>
                                                    </div>
                                                ) : item.sourceType === 'File' ? (
                                                    item.source
                                                ) : (
                                                    item.source || ''
                                                )}
                                            </Text>
                                            <Text className='text-xs text-gray-600 mt-1' noOfLines={2} title={item.text}>
                                                {item.text ? item.text : (item.title || item.fileName || '')}
                                            </Text>
                                        </div>
                                    </div>
                                    <div className='flex items-center gap-3'>
                                        {/* Upload status indicator */}
                                        <div onClick={(e) => e.stopPropagation()}>
                                            {uploadingItemIds.has(item.id) || item.processingStatus === 'Processing' ? (
                                                <Spinner size='sm' />
                                            ) : item.processingStatus === 'Success' ? (
                                                <FontAwesomeIcon
                                                    icon={faCheck}
                                                    style={{ color: '#16a34a', width: 16, height: 16 }}
                                                />
                                            ) : item.processingStatus === 'Failure' ? (
                                                <button
                                                    onClick={() => onRetryLoad(item.id)}
                                                    aria-label='Retry upload'
                                                    className='p-1 rounded'
                                                    style={{ background: 'transparent', border: 'none' }}
                                                >
                                                    <FontAwesomeIcon icon={faRedo} style={{ color: '#f59e0b', width: 16, height: 16 }} />
                                                </button>
                                            ) : null}
                                        </div>


                                        {/* Delete (trash-restore) */}
                                        <div onClick={(e) => e.stopPropagation()}>
                                            <button
                                                onClick={() => deleteSpecificKnowledge(item.id)}
                                                aria-label='Delete source'
                                                className='p-1 rounded'
                                                disabled={uploadingItemIds.has(item.id)}
                                            >
                                                {deleteLoading && knowledgeId === item.id ? (
                                                    <Spinner size='sm' />
                                                ) : (
                                                    <FontAwesomeIcon icon={faTrashRestore} style={{ color: '#ef4444', width: 16, height: 16 }} />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    !knowledgeLoading && (
                        <Flex direction='column' alignItems='center' gap='2' py='8'>
                            <Text className='text-sm text-gray-500'>No knowledge sources uploaded yet.</Text>
                        </Flex>
                    )
                )}

                <AddUrl
                    isOpen={isUrlOpen}
                    onClose={onUrlClose}
                    value={value}
                    loadUrl={loadUrl}
                    setValue={setValue}
                    refetchFrequency={refetchFrequency}
                    setRefetchFrequency={setRefetchFrequency}
                />
                <ReadFullText
                    agentId={agentId}
                    isOpenAddText={isOpenAddText}
                    knowledgeData={knowledgeArray}
                    knowledgeId={knowledgeId}
                    isOpen={isFullTextOpen}
                    onClose={onFullTextClose}
                    fullText={fullText}
                    setFullText={setFullText}
                    onUpdate={fetchKnowledgeData}
                />
                <ConfirmDialog
                    isOpen={isConfirmOpen}
                    onClose={handleCancelDelete}
                    onConfirm={handleConfirmDelete}
                    title={'Delete Source'}
                    description={'Delete this source? This action cannot be undone.'}
                    confirmLabel={'Delete'}
                    cancelLabel={'Cancel'}
                    isLoading={deleteLoading && !!pendingDeleteId && knowledgeId === pendingDeleteId}
                />
            </Collapse>
        </Stack>
    );
};

export default FullAgentConfigKnowledgeBase;
